import { useCallback, useSyncExternalStore } from "react";

const MOVIE_KEY = "fyl-favorite-movies";
const NEWS_KEY = "fyl-favorite-news";

interface FavoriteState {
  movies: string[];
  news: string[];
}

function readIds(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function readState(): FavoriteState {
  return { movies: readIds(MOVIE_KEY), news: readIds(NEWS_KEY) };
}

let cache: FavoriteState = readState();
const listeners = new Set<() => void>();

function commit(next: FavoriteState) {
  cache = next;
  try {
    localStorage.setItem(MOVIE_KEY, JSON.stringify(next.movies));
    localStorage.setItem(NEWS_KEY, JSON.stringify(next.news));
  } catch {
    // 本地存储不可用时仍保留内存状态
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return cache;
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === MOVIE_KEY || event.key === NEWS_KEY) {
      cache = readState();
      listeners.forEach((listener) => listener());
    }
  });
}

export function useFavorites() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const toggleMovie = useCallback((id: string) => {
    const exists = cache.movies.includes(id);
    commit({
      ...cache,
      movies: exists ? cache.movies.filter((item) => item !== id) : [id, ...cache.movies],
    });
  }, []);

  const toggleNews = useCallback((id: string) => {
    const exists = cache.news.includes(id);
    commit({
      ...cache,
      news: exists ? cache.news.filter((item) => item !== id) : [id, ...cache.news],
    });
  }, []);

  const clearAll = useCallback(() => {
    commit({ movies: [], news: [] });
  }, []);

  return {
    movieIds: state.movies,
    newsIds: state.news,
    isMovieFavorite: (id: string) => state.movies.includes(id),
    isNewsFavorite: (id: string) => state.news.includes(id),
    toggleMovie,
    toggleNews,
    clearAll,
    total: state.movies.length + state.news.length,
  };
}